import { z } from 'zod';

import type { IReportItem, IReportSavedView } from '../types/report';
import { ReportComponentHelper } from './ReportComponentHelper';

const monthKeySchema = z.string().regex(/^\d{4}-\d{2}$/);

const periodFilterSchema = z.object({
  grain: z.enum(['month', 'quarter', 'ytd', 'ttm']),
  anchor: monthKeySchema,
  rangeStart: monthKeySchema.nullable().optional(),
  comparison: z.enum(['prior_period', 'prior_year', 'trailing_3_avg']),
  includeOpenMonths: z.boolean().optional(),
});

const periodOverrideSchema = z.object({
  grain: z.enum(['month', 'quarter', 'ytd', 'ttm']).optional(),
  anchor: monthKeySchema.optional(),
  rangeStart: monthKeySchema.nullable().optional(),
  comparison: z
    .enum(['prior_period', 'prior_year', 'trailing_3_avg'])
    .optional(),
  includeOpenMonths: z.boolean().optional(),
});

/** `IReportItem` with the component id still unverified (a string); narrowed in `toView`. */
interface IStoredItem extends Omit<IReportItem, 'componentId'> {
  componentId: string;
}

interface IStoredView extends Omit<IReportSavedView, 'items'> {
  items: IStoredItem[];
}

const savedViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  filter: periodFilterSchema,
  items: z.array(
    z.object({
      instanceId: z.string(),
      componentId: z.string(),
      layout: z.object({
        x: z.number(),
        y: z.number(),
        w: z.number(),
        h: z.number(),
      }),
      override: periodOverrideSchema.nullable().optional(),
    })
  ),
  updatedAt: z.string(),
  isDefault: z.boolean().optional(),
});

/**
 * Persists user-created report views (layout + filter, never data) in
 * localStorage and exposes a subscribe/snapshot pair for `useSyncExternalStore`.
 */
export class SavedViewStorageHelper {
  private static readonly STORAGE_KEY_PREFIX =
    'commenda:management-report:saved-views:v1';

  private static readonly listeners = new Set<() => void>();

  private static readonly cache = new Map<string, IReportSavedView[]>();

  /** Views are scoped per corporation so one entity's layouts never load for another. */
  static storageKey(corporationId: number): string {
    return `${SavedViewStorageHelper.STORAGE_KEY_PREFIX}:${corporationId}`;
  }

  static subscribe(listener: () => void): () => void {
    SavedViewStorageHelper.listeners.add(listener);
    const onStorage = (event: StorageEvent) => {
      if (event.key?.startsWith(SavedViewStorageHelper.STORAGE_KEY_PREFIX)) {
        SavedViewStorageHelper.cache.delete(event.key);
        listener();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      SavedViewStorageHelper.listeners.delete(listener);
      window.removeEventListener('storage', onStorage);
    };
  }

  static getSnapshot(corporationId: number): IReportSavedView[] {
    const key = SavedViewStorageHelper.storageKey(corporationId);
    const cached = SavedViewStorageHelper.cache.get(key);
    if (cached) return cached;
    const views = SavedViewStorageHelper.read(key);
    SavedViewStorageHelper.cache.set(key, views);
    return views;
  }

  static getServerSnapshot(): IReportSavedView[] {
    return SavedViewStorageHelper.EMPTY;
  }

  static persist(corporationId: number, views: IReportSavedView[]): void {
    const key = SavedViewStorageHelper.storageKey(corporationId);
    const custom = views.filter((v) => !v.isDefault);
    window.localStorage.setItem(key, JSON.stringify(custom));
    SavedViewStorageHelper.cache.set(key, custom);
    SavedViewStorageHelper.listeners.forEach((listener) => listener());
  }

  static newViewId(): string {
    return `view-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private static readonly EMPTY: IReportSavedView[] = [];

  private static read(key: string): IReportSavedView[] {
    if (typeof window === 'undefined') return SavedViewStorageHelper.EMPTY;
    const raw = window.localStorage.getItem(key);
    if (!raw) return SavedViewStorageHelper.EMPTY;
    const parsed = z
      .array(savedViewSchema)
      .safeParse(SavedViewStorageHelper.parseJson(raw));
    if (!parsed.success) return SavedViewStorageHelper.EMPTY;
    // Safe: the schema mirrors IStoredView field-for-field; the assertion only
    // restores the required-ness that zod's inference loses without strictNullChecks.
    const views = parsed.data as IStoredView[];
    return views.map(SavedViewStorageHelper.toView);
  }

  private static parseJson(raw: string): unknown {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** Drops items whose component id is no longer in the registry. */
  private static toView(stored: IStoredView): IReportSavedView {
    return {
      ...stored,
      items: stored.items.flatMap((item) => {
        const { componentId } = item;
        return ReportComponentHelper.isComponentId(componentId)
          ? [{ ...item, componentId }]
          : [];
      }),
    };
  }
}
